import { Box } from '@mui/system';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
    useGetApiUserPreferencesParsedQuery,
    usePostApiProjectMutation,
    usePutApiUserPreferencesMutation,
} from 'store/userOrg/enhancedUserOrgApi';
import { useNavigate } from 'react-router-dom';
import { projectDetailsPath } from 'routes/paths';
import { useEffect } from 'react';
import { FormInputText } from 'components/forms/reactHookForm/FormInputText';
import { FormInputSelect } from 'components/forms/reactHookForm/FormInputSelect';
import { FormInputDate } from 'components/forms/reactHookForm/FormInputDate';
import useGAHandleClick from 'hooks/useGAHandleClick';
import { convertDateToUTCStartOfDay } from 'utils/date';
import { throwSuccessToast } from 'utils';
import { successMessages } from 'constants/messages';
import BaseModal from 'components/ui/Modal/BaseModal';
import useModal from 'hooks/useModal';
import { LoadingButton } from '@mui/lab';
import { ProjectCreate } from 'store/userOrg/userOrgApi';
import { IconAdd } from 'utils/icons';

interface ProjectCreationFormProps {
    disabled: boolean;
}

const DEFAULT_VALUES = {
    Name: '',
    ShortName: '',
    Description: '',
    ContactEmail: '',
    StartDate: null,
    EndDate: null,
    DomainId: '',
};

export default function ProjectCreationForm({ disabled }: ProjectCreationFormProps) {
    const googleAnalytics = useGAHandleClick();
    const navigate = useNavigate();
    const { open, handleOpen, handleClose } = useModal();
    const [putPreferences] = usePutApiUserPreferencesMutation();
    const { data: preferences } = useGetApiUserPreferencesParsedQuery();
    const [createProject, { isLoading }] = usePostApiProjectMutation();

    const selectedGlobalFilterValues: string[] = preferences?.globalFilterValues ?? [];

    const methods = useForm({
        defaultValues: DEFAULT_VALUES,
    });

    const {
        handleSubmit,
        reset,
        formState: { isDirty, isValid },
    } = methods;

    useEffect(() => {
        if (!open) {
            reset(DEFAULT_VALUES);
        }
    }, [open]);

    const onSubmit = async (data: any) => {
        const payload: ProjectCreate = {
            ...data,
            StartDate: data.StartDate ? convertDateToUTCStartOfDay(new Date(data.StartDate)) : undefined,
            EndDate: data.EndDate ? convertDateToUTCStartOfDay(new Date(data.EndDate)) : undefined,
            DomainId: data.DomainId || null,
        };

        await createProject({
            projectCreate: payload,
        }).unwrap();

        throwSuccessToast({ message: successMessages.created });

        await putPreferences({
            body: {
                ...preferences,
                globalFilterValues: [...selectedGlobalFilterValues, payload.ShortName as string],
            },
        });

        googleAnalytics({
            label: 'CreateProject',
            action: 'CreateProject',
            category: 'create',
        });

        handleClose();
        navigate(projectDetailsPath(payload.ShortName as string));
    };

    return (
        <>
            <Button
                disabled={disabled}
                variant='contained'
                onClick={() => {
                    handleOpen();
                    googleAnalytics({
                        label: 'OpenCreateProjectModal',
                        action: 'Open',
                        category: 'Modal',
                    });
                }}
                startIcon={<IconAdd size={20} />}>
                Create Project
            </Button>
            <BaseModal
                fullWidth
                open={open}
                onClose={handleClose}
                title='Create project'
                actions={
                    <>
                        <Button variant='outlined' onClick={handleClose}>
                            Cancel
                        </Button>
                        <LoadingButton
                            color='primary'
                            variant='contained'
                            loading={isLoading}
                            disabled={!isDirty || !isValid}
                            onClick={handleSubmit(onSubmit)}>
                            Confirm
                        </LoadingButton>
                    </>
                }>
                <Box>
                    <FormProvider {...methods}>
                        <FormInputText name='Name' required label='Name' margin='normal' fullWidth maxLength={100} />
                        <FormInputText
                            name='ShortName'
                            maxLength={50}
                            required
                            label='Short Name'
                            margin='normal'
                            fullWidth
                            regex={/^\S+$/}
                            regexErrorMessage='Short Name must be one word (no spaces)'
                        />
                        <FormInputText
                            name='Description'
                            required
                            multiline
                            maxRows={5}
                            maxLength={150}
                            minLength={5}
                            label='Description'
                            margin='normal'
                            fullWidth
                        />
                        <FormInputText
                            name='ContactEmail'
                            required
                            type='email'
                            maxLength={100}
                            label='Contact Email'
                            margin='normal'
                            fullWidth
                        />
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <FormInputSelect
                                label='Domain'
                                name='DomainId'
                                required
                                options={[
                                    { value: 1, label: 'Veterinary Sciences' },
                                    { value: 2, label: 'Engineering and Technology' },
                                    { value: 3, label: 'Natural Sciences' },
                                    { value: 4, label: 'Medical and Health Sciences' },
                                    { value: 5, label: 'Others' },
                                ]}
                                fullWidth
                            />
                        </Box>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box sx={{ display: 'flex', gap: 3, flexDirection: 'column' }}>
                                <FormInputDate
                                    name='StartDate'
                                    label='Start Date'
                                    required
                                    maxDateFieldName='EndDate'
                                />
                                <FormInputDate name='EndDate' label='End Date' required minDateFieldName='StartDate' />
                            </Box>
                        </LocalizationProvider>
                    </FormProvider>
                </Box>
            </BaseModal>
        </>
    );
}
